import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loginUser } from '../utils/api';

/* ─────────────────────────────────────────────────────
   DHA LOGIN PAGE v3 — Teal/Blue centered card design
   Icons: realistic stethoscope + syringe (SVG)
   Logo: DHA official crest (crescent, palm, buildings)
───────────────────────────────────────────────────── */
export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password.trim()) {
      toast.error('Please enter your username and password');
      return;
    }
    setLoading(true);
    try {
      const res = await loginUser(form);
      localStorage.setItem('dha_token', res.data.token);
      localStorage.setItem('dha_role', res.data.user.role);
      localStorage.setItem('dha_name', res.data.user.full_name || res.data.user.username);
      if (onLogin) onLogin();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0d2137 0%, #0e6b8a 55%, #0891b2 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Segoe UI', sans-serif",
      position: 'relative', overflow: 'hidden',
    }}>

      <style>{`
        @keyframes ecgDraw { 0%{stroke-dashoffset:700} 100%{stroke-dashoffset:0} }
        .ecg-anim { stroke-dasharray:700; animation:ecgDraw 3.8s linear infinite; }
        .login-input:focus { border-color:#0e6b8a !important; box-shadow:0 0 0 3px rgba(14,107,138,0.15) !important; outline:none; }
        .sign-btn:hover:not(:disabled) { background:#0a5570 !important; transform:translateY(-1px); box-shadow:0 8px 22px rgba(14,107,138,0.4) !important; }
        .sign-btn { transition:all .2s ease !important; }
      `}</style>

      {/* ═══ FLOATING BACKGROUND CIRCLES ═══ */}

      {/* TOP-LEFT: Doctor's Medical Bag */}
      <div style={{
        position:'absolute', top:-85, left:-85, width:330, height:330, borderRadius:'50%',
        background:'rgba(255,255,255,0.055)', border:'1.5px solid rgba(255,255,255,0.13)',
        display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none',
      }}>
        <div style={{
          width:205, height:205, borderRadius:'50%',
          background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.09)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <svg width="105" height="105" viewBox="0 0 100 100" fill="none">
            {/* Bag handle */}
            <path d="M36 32 L36 24 C36 18 44 14 50 14 C56 14 64 18 64 24 L64 32"
              stroke="rgba(255,255,255,0.6)" strokeWidth="4.5" fill="none" strokeLinecap="round"/>
            {/* Bag body */}
            <rect x="14" y="32" width="72" height="54" rx="10"
              fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.58)" strokeWidth="3"/>
            {/* Bag latch/clasp strip */}
            <rect x="14" y="44" width="72" height="8" rx="0"
              fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
            {/* Medical cross — vertical */}
            <rect x="47" y="56" width="6" height="24" rx="3" fill="rgba(255,255,255,0.7)"/>
            {/* Medical cross — horizontal */}
            <rect x="38" y="65" width="24" height="6" rx="3" fill="rgba(255,255,255,0.7)"/>
          </svg>
        </div>
      </div>

      {/* BOTTOM-LEFT: ECG waveform */}
      <div style={{
        position:'absolute', bottom:75, left:50, width:165, height:165, borderRadius:'50%',
        background:'rgba(255,255,255,0.045)', border:'1px solid rgba(255,255,255,0.09)',
        display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none',
      }}>
        <svg width="100" height="56" viewBox="0 0 100 56" fill="none">
          <polyline className="ecg-anim"
            points="0,38 14,38 20,38 25,22 31,38 36,38 42,5 46,54 50,38 56,26 62,38 78,38 100,38"
            stroke="rgba(255,255,255,0.6)" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* TOP-RIGHT: Realistic Syringe */}
      <div style={{
        position:'absolute', top:-58, right:-58, width:295, height:295, borderRadius:'50%',
        background:'rgba(255,255,255,0.055)', border:'1.5px solid rgba(255,255,255,0.13)',
        display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none',
      }}>
        <div style={{
          width:188, height:188, borderRadius:'50%',
          background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.09)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          {/* Syringe rotated 45° */}
          <svg width="118" height="118" viewBox="0 0 100 100" fill="none">
            <g transform="rotate(-45 50 50)">
              {/* Needle */}
              <line x1="84" y1="50" x2="100" y2="50" stroke="rgba(255,255,255,0.48)" strokeWidth="1.6" strokeLinecap="round"/>
              {/* Needle hub */}
              <path d="M76 44 L84 50 L76 56 Z" fill="rgba(255,255,255,0.42)"/>
              {/* Barrel */}
              <rect x="20" y="42" width="56" height="16" rx="4" stroke="rgba(255,255,255,0.58)" strokeWidth="2.3" fill="rgba(255,255,255,0.07)"/>
              {/* Barrel end flange */}
              <rect x="18" y="37" width="5" height="26" rx="2.5" fill="rgba(255,255,255,0.38)"/>
              {/* Liquid fill */}
              <rect x="21.5" y="43.5" width="27" height="13" rx="3" fill="rgba(255,255,255,0.22)"/>
              {/* Graduation marks */}
              <line x1="34" y1="43" x2="34" y2="59" stroke="rgba(255,255,255,0.4)" strokeWidth="1.4"/>
              <line x1="47" y1="43" x2="47" y2="59" stroke="rgba(255,255,255,0.4)" strokeWidth="1.4"/>
              <line x1="60" y1="43" x2="60" y2="59" stroke="rgba(255,255,255,0.4)" strokeWidth="1.4"/>
              {/* Plunger disk */}
              <rect x="13" y="42" width="8" height="16" rx="2.5" fill="rgba(255,255,255,0.52)"/>
              {/* Plunger rod */}
              <line x1="2" y1="50" x2="13" y2="50" stroke="rgba(255,255,255,0.52)" strokeWidth="3.5" strokeLinecap="round"/>
              {/* T-handle */}
              <rect x="0" y="43" width="5" height="14" rx="2.5" fill="rgba(255,255,255,0.48)"/>
            </g>
          </svg>
        </div>
      </div>

      {/* BOTTOM-RIGHT: Medical Cross */}
      <div style={{
        position:'absolute', bottom:-42, right:85, width:198, height:198, borderRadius:'50%',
        background:'rgba(255,255,255,0.045)', border:'1px solid rgba(255,255,255,0.09)',
        display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none',
      }}>
        <svg width="74" height="74" viewBox="0 0 74 74" fill="none">
          <rect x="25" y="5"  width="24" height="64" rx="6" fill="rgba(255,255,255,0.16)" stroke="rgba(255,255,255,0.55)" strokeWidth="2.2"/>
          <rect x="5"  y="25" width="64" height="24" rx="6" fill="rgba(255,255,255,0.16)" stroke="rgba(255,255,255,0.55)" strokeWidth="2.2"/>
        </svg>
      </div>

      {/* ═══ ECG BOTTOM BORDER ═══ */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, pointerEvents:'none' }}>
        <svg viewBox="0 0 1440 68" style={{ width:'100%', display:'block' }} preserveAspectRatio="none">
          <polyline className="ecg-anim"
            points="0,46 95,46 175,46 207,28 233,46 258,46 290,4 307,66 322,46 350,32 378,46 530,46 615,46 647,28 673,46 698,46 730,4 747,66 762,46 790,32 818,46 970,46 1055,46 1087,28 1113,46 1138,46 1170,4 1187,66 1202,46 1230,32 1258,46 1440,46"
            stroke="rgba(255,255,255,0.18)" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* ═══════════════════════════════════════
           CENTER WHITE LOGIN CARD
      ═══════════════════════════════════════ */}
      <div style={{
        position:'relative', zIndex:10,
        background:'rgba(255,255,255,0.975)',
        borderRadius:22, padding:'38px 38px 30px',
        width:'100%', maxWidth:424,
        boxShadow:'0 30px 90px rgba(0,0,0,0.38)',
      }}>

        {/* ── LOGOS: CARELINE (left corner) + DHA (right corner) ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <img
            src="/careline-logo.png"
            alt="CARELINE"
            style={{ height:48, objectFit:'contain' }}
          />
          <img
            src="/dha-logo.png"
            alt="DHA Karachi"
            style={{ height:78, objectFit:'contain' }}
          />
        </div>

        {/* Hospital name */}
        <div style={{ textAlign:'center', marginBottom:4 }}>
          <div style={{ fontSize:10, color:'#64748b', letterSpacing:2.5, marginBottom:3 }}>WELCOME TO</div>
          <div style={{ fontSize:21, fontWeight:900, color:'#0d2137' }}>DHA Medical Centre</div>
          <div style={{ display:'flex', height:3, width:88, borderRadius:2, overflow:'hidden', margin:'8px auto 0' }}>
            <div style={{ flex:3, background:'#1a6b3c' }}/>
            <div style={{ width:7, background:'#fff', borderLeft:'1px solid #dde', borderRight:'1px solid #dde' }}/>
            <div style={{ flex:1, background:'#1a6b3c' }}/>
          </div>
        </div>

        {/* CARELINE */}
        <div style={{ textAlign:'center', marginBottom:22, marginTop:8 }}>
          <span style={{ fontSize:10.5, color:'#94a3b8' }}>In Collaboration with </span>
          <span style={{ fontSize:11.5, fontWeight:900, color:'#0d2137', letterSpacing:1 }}>CARE</span>
          <span style={{ fontSize:11.5, fontWeight:900, color:'#c41e3a', letterSpacing:1 }}>LINE</span>
          <span style={{ fontSize:10.5, color:'#94a3b8' }}> · One Heartbeat Away</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#0e6b8a', letterSpacing:1, marginBottom:6 }}>
              USERNAME
            </label>
            <div style={{ display:'flex', alignItems:'center', border:'1.5px solid #e2e8f0', borderRadius:10, background:'#f8fafc' }}>
              <span style={{ padding:'0 14px', color:'#94a3b8', fontSize:18 }}>👤</span>
              <input className="login-input" type="text" value={form.username} autoFocus
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                placeholder="Enter username"
                style={{ flex:1, padding:'12px 10px 12px 6px', border:'none', outline:'none', fontSize:14, color:'#0d2137', background:'transparent' }}
              />
            </div>
          </div>

          <div style={{ marginBottom:6 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#0e6b8a', letterSpacing:1, marginBottom:6 }}>
              PASSWORD
            </label>
            <div style={{ display:'flex', alignItems:'center', border:'1.5px solid #e2e8f0', borderRadius:10, background:'#f8fafc' }}>
              <span style={{ padding:'0 14px', color:'#94a3b8', fontSize:18 }}>🔒</span>
              <input className="login-input" type={showPass ? 'text' : 'password'} value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Enter password"
                style={{ flex:1, padding:'12px 4px 12px 6px', border:'none', outline:'none', fontSize:14, color:'#0d2137', background:'transparent' }}
              />
              <span onClick={() => setShowPass(p => !p)} style={{ padding:'0 12px', color:'#94a3b8', cursor:'pointer', userSelect:'none', display:'flex', alignItems:'center' }}>
                {showPass ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </span>
            </div>
          </div>

          <div style={{ textAlign:'right', marginBottom:18 }}>
            <Link to="/forgot-password" style={{ fontSize:12, color:'#c41e3a', textDecoration:'none', fontWeight:600 }}>
              Forgot Password?
            </Link>
          </div>

          <button type="submit" className="sign-btn" disabled={loading} style={{
            width:'100%', padding:'13px',
            background: loading ? '#64748b' : '#0e6b8a',
            color:'#fff', border:'none', borderRadius:10,
            fontSize:14, fontWeight:800, letterSpacing:2,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}>
            {loading ? 'Verifying...' : 'SIGN IN →'}
          </button>
        </form>

        <div style={{ marginTop:20, paddingTop:14, borderTop:'1px solid #f1f5f9', textAlign:'center' }}>
          <div style={{ fontSize:10, color:'#94a3b8' }}>
            📍 DHA, Karachi &nbsp;|&nbsp; 📞 +92 310 2145333
          </div>
        </div>
      </div>
    </div>
  );
}
